import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Container, Row, Col, Form, Button, Table, Badge, Spinner } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaCalculator, FaHotel, FaCheckCircle, FaInfoCircle, FaArrowRight } from "react-icons/fa";
import axios from "axios";

import Header from "../components/Header";
import Footer from "../components/Footer";
import API_BASE_URL from "../config";

const FeesCalculator = () => {
  // Form State
  const [form, setForm] = useState({
    category: "",
    program: "",
    accommodation: false,
    occupancy: ""
  });

  // Data State
  const [feeStructureData, setFeeStructureData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fetch/feeStructureData`);
      if (response.data?.[0]) setFeeStructureData(response.data[0]);
    } catch (error) {
      console.error("Sync Error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Derived Programs List
  const availablePrograms = useMemo(() => {
    if (!feeStructureData) return [];
    if (form.category === "Undergraduate") return feeStructureData.undergraduatePrograms[0]?.programNames || [];
    if (form.category === "Graduate") {
      return feeStructureData.graduatePrograms.flatMap(g => g.programNames) || [];
    }
    if (form.category === "Additional") return feeStructureData.additionalCourses?.map(c => c.courseName) || [];
    return [];
  }, [feeStructureData, form.category]);

  const occupancyOptions = feeStructureData ? Object.keys(feeStructureData.hostelCharges) : [];

  const handleCalculate = (e) => {
    e.preventDefault();
    let details = null;

    if (form.category === "Undergraduate") {
      details = feeStructureData.undergraduatePrograms[0].financialYear;
    } else if (form.category === "Graduate") {
      details = feeStructureData.graduatePrograms.find(p => p.programNames.includes(form.program))?.financialYear;
    } else if (form.category === "Additional") {
      details = feeStructureData.additionalCourses.find(c => c.courseName === form.program)?.financialYear;
    }

    if (details) {
      // Normalize data to ensure totalFeesAtAdmission is always present
      const normalizedDetails = { ...details };
      
      if (!normalizedDetails.totalFeesAtAdmission) {
        if (form.category === "Graduate") {
          // For Graduate, "At Admission" total is usually Admission Fee + 1 Course Tuition Fee
          normalizedDetails.totalFeesAtAdmission = (normalizedDetails.admissionFee || 0) + (normalizedDetails.tuitionFeePerCourse || 0);
        } else if (form.category === "Additional") {
          normalizedDetails.totalFeesAtAdmission = normalizedDetails.fee || 0;
        }
      }

      const hostelFee = form.accommodation ? (feeStructureData.hostelCharges[form.occupancy] || 0) : 0;
      setResults({ ...normalizedDetails, hostelFee, programName: form.program });
    }
  };

  if (loading) return (
    <div className="premium-loader">
      <Spinner animation="grow" variant="primary" />
      <p className="mt-3 fw-bold text-navy letter-spacing-1">INITIALIZING CALCULATOR</p>
    </div>
  );

  return (
    <div className="portal-master-wrapper bg-light-soft min-vh-100">
      <Header />
      {/* --- SUB-NAV BAR --- */}
      <section className="portal-sub-nav py-4 shadow-sm bg-white sticky-top no-print" style={{ top: '0', zIndex: 1020 }}>
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <h1 className="h4 fw-900 text-navy m-0">Fees <span className="text-primary">Calculator</span></h1>
              <small className="text-muted d-flex align-items-center gap-2 mt-1">
                 <span className="sync-dot-mini"></span> Dynamic Cost Estimator
              </small>
            </Col>
            <Col md={6} className="text-md-end">
              <Badge bg="soft-primary" className="text-primary px-3 py-2 rounded-pill fw-bold">Academic Session 2024-25</Badge>
            </Col>
          </Row>
        </Container>
      </section>
                
      <section className="portal-viewport bg-light-soft py-5">
        <Container className="main-portal-container section-standard-spacing position-relative" style={{ zIndex: 10 }}>
          <Row className="g-4">
            {/* Left Side: Calculator Input */}
            <Col lg={5}>
              <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
                <div className="sidebar-card border-0 shadow-sm">
                  <div className="card-label">ESTIMATOR CRITERIA</div>
                  <div className="p-4 p-lg-5">
                    <Form onSubmit={handleCalculate}>
                      <Form.Group className="mb-4">
                        <Form.Label className="category-tag mb-2">Academic Level</Form.Label>
                        <Form.Select 
                          className="custom-input-pill"
                          value={form.category} 
                          onChange={(e) => setForm({...form, category: e.target.value, program: ""})}
                          required
                        >
                          <option value="">Select Category</option>
                          <option value="Undergraduate">Undergraduate</option>
                          <option value="Graduate">Graduate Programs</option>
                          <option value="Additional">Additional Courses</option>
                        </Form.Select>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label className="category-tag mb-2">Target Program</Form.Label>
                        <Form.Select 
                          className="custom-input-pill"
                          value={form.program}
                          disabled={!form.category}
                          onChange={(e) => setForm({...form, program: e.target.value})}
                          required
                        >
                          <option value="">Select Program</option>
                          {availablePrograms.map((p, i) => <option key={i} value={p}>{p}</option>)}
                        </Form.Select>
                      </Form.Group>

                      <div className="accommodation-section p-4 rounded-4 mb-4 bg-light-soft border">
                        <Form.Check 
                          type="switch"
                          id="hostel-switch"
                          label="Hostel Accommodation"
                          className="fw-bold text-navy"
                          checked={form.accommodation}
                          onChange={(e) => setForm({...form, accommodation: e.target.checked, occupancy: ""})}
                        />
                    
                        <AnimatePresence>
                          {form.accommodation && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                              <Form.Group className="mt-4">
                                <Form.Label className="small fw-bold text-muted mb-2">ROOM TYPE Preference</Form.Label>
                                <Form.Select 
                                  className="custom-input-pill"
                                  value={form.occupancy}
                                  onChange={(e) => setForm({...form, occupancy: e.target.value})}
                                  required={form.accommodation}
                                >
                                  <option value="">Select Room Type</option>
                                  {occupancyOptions.map((o, i) => <option key={i} value={o}>{o}</option>)}
                                </Form.Select>
                              </Form.Group>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      <Button variant="primary" type="submit" className="w-100 py-3 rounded-pill fw-bold shadow-sm d-flex align-items-center justify-content-center">
                        CALCULATE ESTIMATE <FaArrowRight className="ms-2" />
                      </Button>
                    </Form>
                  </div>
                </div>
              </motion.div>
            </Col>

          {/* Right Side: Results Display */}
          <Col lg={7}>
            <AnimatePresence mode="wait">
              {results ? (
                <motion.div key={results.programName} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="details-card bg-white p-4 p-lg-5 rounded-4 shadow-sm h-100">
                    <div className="section-intro mb-5">
                      <div className="category-tag mb-2">Estimated Total</div>
                      <h2 className="portal-heading text-navy display-5 mb-3">PKR {(results.totalFeesAtAdmission + results.hostelFee).toLocaleString()}</h2>
                      <div className="ivy-divider mb-4" />
                      <div className="d-flex align-items-center gap-3">
                         <Badge bg="primary" className="px-3 py-2 rounded-pill fw-bold">{results.programName}</Badge>
                         <p className="small text-muted mb-0">Total Payable at Admission</p>
                      </div>
                    </div>

                    <div className="breakdown-wrapper rounded-4 border overflow-hidden">
                      <div className="px-4 py-3 bg-light border-bottom d-flex justify-content-between align-items-center">
                        <span className="fw-800 text-primary small text-uppercase ls-1">Detailed Breakdown</span>
                        <FaCheckCircle className="text-success" />
                      </div>
                      <Table responsive className="m-0 premium-fee-table">
                        <tbody>
                          {Object.entries(results).map(([key, value]) => {
                            if (["year", "remarks", "hostelFee", "programName"].includes(key)) return null;
                            return (
                              <tr key={key} className="fee-row">
                                <td className="text-muted fw-500 py-3 fee-label px-4 text-uppercase small">{key.replace(/([A-Z])/g, " $1")}</td>
                                <td className="text-end fw-bold text-navy py-3 fee-value px-4">PKR {value?.toLocaleString()}</td>
                              </tr>
                            );
                          })}
                          {results.hostelFee > 0 && (
                            <tr className="bg-light-soft fee-row">
                              <td className="py-3 px-4 fw-bold text-primary fee-label"><FaHotel className="me-2" /> HOSTEL CHARGES</td>
                              <td className="text-end fw-bold text-primary py-3 fee-value px-4">PKR {results.hostelFee.toLocaleString()}</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {results.remarks && results.remarks.length > 0 && (
                        <div className="mt-5 p-4 rounded-4 bg-light-soft border-start border-warning border-4">
                            <h6 className="fw-bold text-navy mb-3 d-flex align-items-center">
                                <FaInfoCircle className="me-2 text-warning" /> Important Application Notes
                            </h6>
                            <ul className="list-unstyled mb-0">
                                {results.remarks
                                .filter(r => r.toLowerCase().includes(results.programName.toLowerCase()))
                                .map((r, i) => (
                                    <li key={i} className="small text-muted mb-2 d-flex align-items-start">
                                        <FaArrowRight className="mt-1 me-2 text-primary" style={{fontSize: '0.6rem'}} />
                                        <span>{r}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </motion.div>
              ) : (
                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-center p-5 border-dashed rounded-4 details-card">
                   <div className="mb-4 opacity-20">
                     <FaCalculator size={80} className="text-navy" />
                   </div>
                   <h4 className="text-navy fw-900">Ready to Estimate?</h4>
                   <p className="text-muted px-lg-5">Configure your academic track and preferences on the left to generate a precise admission fee breakdown synced with official SIBA records.</p>
                </div>
              )}
            </AnimatePresence>
          </Col>
        </Row>
      </Container>
    </section>
      <Footer />

      <style>{`
        .portal-master-wrapper { background-color: #fff; min-height: 100vh; font-family: 'Inter', sans-serif; overflow-x: hidden; }
        .bg-light-soft { background-color: #f8faff; }
        .fw-900 { font-weight: 900; }
        .fw-800 { font-weight: 800; }
        .text-navy { color: #002b5a; }
        .ls-1 { letter-spacing: 1px; }

        /* Typography Sync */
        .category-tag { color: #0055a4; font-weight: 800; text-transform: uppercase; font-size: 0.8rem; letter-spacing: 2px; }
        .portal-heading { font-weight: 900; letter-spacing: -1.5px; line-height: 1.1; }
        .ivy-divider { width: 60px; height: 5px; background: #0055a4; border-radius: 10px; }
        
        .sync-dot-mini {
          width: 8px; height: 8px; background: #10b981;
          border-radius: 50%; display: inline-block;
          animation: pulse-green 2s infinite;
        }
        @keyframes pulse-green {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 5px rgba(16, 185, 129, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }

        .main-portal-container { margin-top: 0; padding-bottom: 100px; position: relative; z-index: 10; }
        
        .sidebar-card { background: #fff; border-radius: 24px; overflow: hidden; display: flex; flex-direction: column; border: 1px solid #e2e8f0 !important; }
        .card-label { 
          padding: 18px 25px; background: #002b5a; color: white; 
          font-weight: 800; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase;
        }

        .custom-input-pill {
          border-radius: 12px; padding: 12px 20px; border: 2px solid #f1f5f9;
          font-weight: 600; color: #1e293b; transition: all 0.3s;
        }
        .custom-input-pill:focus { border-color: #0055a4; box-shadow: 0 0 0 4px rgba(0, 85, 164, 0.05); outline: none; }

        .details-card { border-radius: 24px !important; border: 1px solid #e2e8f0 !important; }
        .border-dashed { border: 2px dashed #e2e8f0 !important; }

        .premium-fee-table tbody td { vertical-align: middle; border-bottom: 1px solid #f1f5f9; }
        .fee-row:last-child td { border-bottom: none; }

        .premium-loader { height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #fff; }

        @media (max-width: 991px) {
          .main-portal-container { margin-top: 0px; }
          .portal-heading { font-size: 1.8rem; letter-spacing: -1px; }
          .category-tag { font-size: 0.7rem; letter-spacing: 1.5px; }
          .details-card { padding: 1rem !important; margin-bottom: 2rem; }
          .section-intro { margin-bottom: 2rem !important; }
          
          /* Mobile Table Density */
          .premium-fee-table { display: block; width: 100%; }
          .premium-fee-table tbody, .premium-fee-table tr, .premium-fee-table td { display: block; width: 100%; }
          .fee-row { padding: 8px 0; border-bottom: 1px solid #f8faff; }
          .fee-label { font-size: 0.65rem; color: #94a3b8 !important; padding: 0 !important; }
          .fee-value { text-align: left !important; padding: 0 !important; font-size: 0.9rem; margin-top: 2px; }
          
          .breakdown-wrapper { padding: 10px; }
          .breakdown-wrapper .px-4 { padding-left: 10px !important; padding-right: 10px !important; }
          
          .custom-input-pill { padding: 10px 15px; font-size: 0.9rem; }
          .sidebar-card .p-4 { padding: 1.25rem !important; }
          
          .portal-sub-nav .h4 { font-size: 1.25rem; }
          .portal-sub-nav .sync-dot-mini { transform: scale(0.8); }
        }

        @media (max-width: 480px) {
          .portal-heading { font-size: 1.6rem; }
          .display-6 { font-size: 1.5rem !important; }
        }
      `}</style>
    </div>
  );
};

export default FeesCalculator;